import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppState } from '../state/AppState';
import {
  fetchSessionData,
  getContinueInvalidAttributeError,
  getProviderSetupIncompleteMessage,
  PROVIDER_SETUP_INCOMPLETE_TITLE,
} from '../services/sessionService';
import { initializeConfig } from '../services/configService';

const FIND_A_PROVIDER_URL = 'https://mindfultherapygroup.com/find-a-provider/';

export type LoadingStep = 
  | 'continue'
  | 'session/all'
  | 'availabilities'
  | 'list'
  | 'complete';


/**
 * Custom hook to enforce strict loading order for the application.
 * 
 * Loading order:
 * 1. continue
 * 2. session/all
 * 3. availabilities  
 * 4. list
 * 
 * Pages will not render until all required data is loaded in the correct order.
 */
export function useStrictLoadingOrder() {
  const { state, dispatch } = useAppState();
  const navigate = useNavigate();
  const [error, setError] = useState<string | null>(null);
  const [isResuming, setIsResuming] = useState<boolean>(false);
  const [show400Error, setShow400Error] = useState<boolean>(false);
  const [errorTitle, setErrorTitle] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  /** When set, "Go Back" runs this instead of `navigate(-1)`. Cleared on close. */
  const [errorModalGoBack, setErrorModalGoBack] = useState<(() => void) | null>(null);

  const openErrorModal = (title: string, message: string, goBackOverride?: (() => void) | null) => {
    setErrorTitle(title);
    setErrorMessage(message);
    setShow400Error(true);
    // React treats functions passed to setState as updater callbacks.
    // Wrap so we can store a function value without executing it.
    if (goBackOverride) {
      setErrorModalGoBack(() => goBackOverride);
    } else {
      setErrorModalGoBack(null);
    }
  };

  const pushIoiErrorToDataLayer = (errorMessage: string) => {
    if (typeof window === 'undefined') return;
    const dl = (window as any).dataLayer;
    if (!dl?.push) return;
    dl.push({
      event: 'IOI Error',
      'IOI Error Message': errorMessage
    });
  };

  const openProviderBookedModal = (payload?: Record<string, unknown>) => {
    if (payload) {
      pushIoiErrorToDataLayer(`Invalid Payload: ${JSON.stringify(payload)}`);
    }
    openErrorModal('Provider Booked', 'The provider is fully booked. Please find another provider.', () => {
      window.location.assign(FIND_A_PROVIDER_URL);
    });
  };

  const openProviderSetupIncompleteModal = (message?: string) => {
    openErrorModal(
      PROVIDER_SETUP_INCOMPLETE_TITLE,
      message || getProviderSetupIncompleteMessage()
    );
  };

  useEffect(() => {
    const loadDataInOrder = async () => {
      try {
        setError(null);

        // Step 1: continue
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: true, loadingStep: 'continue', loadingProgress: 10 } });

        const config = await initializeConfig();
        const baseUrl = config.remoteApiUrl || 'http://localhost:5293/';
        
        // Check if we have continue flow params
        const urlParams = new URLSearchParams(window.location.search);
        const hasMtgSession = urlParams.has('mtgSession');
        const hasSessionTimeout = urlParams.has('sessionTimeout');
        const hasSessionExtensionTime = urlParams.has('sessionExtensionTime');
        const hasTimerStart = urlParams.has('timerStart');
        
        const isContinueFlow = hasMtgSession || hasSessionTimeout || hasSessionExtensionTime || hasTimerStart;
        const isPageRefresh = !isContinueFlow;
        
        let resolvedProviderId = config.providerId;
        let continueResponseData: any = null; // Track continue response data
        let sessionData: any = null;
        let hasStateFromContinue = false; // Track if continue response provided state/region
        let continueResponseStateString: string | null = null; // Track stateString directly from continue response
        
        if (isContinueFlow) {
          // Save mtgSession to state for later use
          const mtgSessionFromQuery = urlParams.get('mtgSession');
          if (mtgSessionFromQuery) {
            dispatch({ type: 'SET_MTG_SESSION', payload: mtgSessionFromQuery });
          }
          
          // Call continue API FIRST (this is the /api/Session/continue call)
          const { getProviderData } = await import('../services/sessionService');
          
          if (mtgSessionFromQuery) {
            try {
              const providerData = await getProviderData(mtgSessionFromQuery, baseUrl);

              const setupError = getContinueInvalidAttributeError(providerData);
              if (setupError) {
                openProviderSetupIncompleteModal(setupError);
                dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
                return;
              }
              
              if (providerData.success && providerData.data) {
              // Set the data directly in state
              const data = providerData.data;
              if (data.contactId) {
                resolvedProviderId = data.contactId.toString();
                dispatch({ type: 'SET_PROVIDER_ID', payload: resolvedProviderId! });
              }
              if (data.locationState) {
                // Check if locationState contains comma (multiple states) - treat as null
                if (data.locationState.includes(',')) {
                  // Don't set stateString and don't mark as having state from continue
                } else {
                  continueResponseStateString = data.locationState; // Track the value directly
                  dispatch({ type: 'SET_STATE_STRING', payload: data.locationState });
                  hasStateFromContinue = true;
                }
              }
              if (data.insurances) {
                dispatch({ type: 'SET_INSURANCE', payload: data.insurances });
              }
              if (data.specialties) {
                dispatch({ type: 'SET_REASON', payload: data.specialties });
              }
              if (data.service) {
                dispatch({ type: 'SET_SERVICE', payload: data.service });
              }
              if (data.ageRange) {
                dispatch({ type: 'SET_AGE_GROUP', payload: data.ageRange });
              }
              if (data.columnId) {
                dispatch({ type: 'SET_COLUMN_ID', payload: data.columnId });
              }
              if (data.profileId) {
                dispatch({ type: 'SET_PROFILE_ID', payload: data.profileId });
              }
              if (data.licenseKey) {
                dispatch({ type: 'SET_LICENSE_KEY', payload: data.licenseKey });
              }
              if (data.phone) {
                dispatch({ type: 'SET_PHONE', payload: data.phone });
              }
              if (data.textConsent !== undefined) {
                dispatch({ type: 'SET_TEXT_CONSENT', payload: data.textConsent });
              }
              
              if (providerData.originalData?.IOI_Payload?.cs_modalities != null && providerData.originalData.IOI_Payload.cs_modalities !== '') {
                const modalities = typeof providerData.originalData.IOI_Payload.cs_modalities === 'string'
                  ? decodeURIComponent(providerData.originalData.IOI_Payload.cs_modalities)
                  : String(providerData.originalData.IOI_Payload.cs_modalities);
                dispatch({ type: 'SET_MODALITIES', payload: modalities });
              }
              
              continueResponseData = { processed: true };
            }
            } catch (providerError: any) {
              // Handle 400 errors specifically
              if (providerError?.status === 400) {
                // Use a user-friendly error message instead of technical details
                openErrorModal('Technical Issues', 'An error has occurred, please try again.');
                dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
                return; // Stop loading process
              }
              // Re-throw other errors to be handled by outer catch
              throw providerError;
            }
          }
          
          // Check if continue flow provided state/region
          if (isContinueFlow && !hasStateFromContinue) {
            // Continue to load availabilities but skip location processing
          }
        }

        // Set providerId in state
        if (resolvedProviderId) {
          dispatch({ type: 'SET_PROVIDER_ID', payload: resolvedProviderId });
        }

        // Step 2: session/all
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: true, loadingStep: 'session/all', loadingProgress: 30 } });

        // Fetch session data (always needed for location processing)
        if (!sessionData) {
          sessionData = await fetchSessionData(baseUrl);
          dispatch({ type: 'SET_SESSION_DATA', payload: sessionData });
        }

        // Load session data into state (only for page refresh flow)
        if (isPageRefresh && sessionData?.data?.data) {
          try {
            const parsedData = JSON.parse(sessionData.data.data);

            // Load session data into state
            if (parsedData.reason) dispatch({ type: 'SET_REASON', payload: parsedData.reason });
            if (parsedData.service) dispatch({ type: 'SET_SERVICE', payload: parsedData.service });
            if (parsedData.insurance) dispatch({ type: 'SET_INSURANCE', payload: parsedData.insurance });
            if (parsedData.location) dispatch({ type: 'SET_LOCATION', payload: parsedData.location });
            if (parsedData.locationCode) dispatch({ type: 'SET_LOCATION_CODE', payload: parsedData.locationCode });
            if (parsedData.providerId) {
              resolvedProviderId = parsedData.providerId; // Update resolvedProviderId for refresh flow
              dispatch({ type: 'SET_PROVIDER_ID', payload: parsedData.providerId });
            }
            if (parsedData.stateString) dispatch({ type: 'SET_STATE_STRING', payload: parsedData.stateString });
            if (parsedData.visitType) dispatch({ type: 'SET_VISIT_TYPE', payload: parsedData.visitType });
            if (parsedData.appointmentDate) dispatch({ type: 'SET_APPOINTMENT_DATE', payload: parsedData.appointmentDate });
            if (parsedData.appointmentTime) dispatch({ type: 'SET_APPOINTMENT_TIME', payload: parsedData.appointmentTime });
            if (parsedData.appointmentDay) dispatch({ type: 'SET_APPOINTMENT_DAY', payload: parsedData.appointmentDay });
            if (parsedData.profileId) dispatch({ type: 'SET_PROFILE_ID', payload: parsedData.profileId });
            if (parsedData.columnId) dispatch({ type: 'SET_COLUMN_ID', payload: parsedData.columnId });
            if (parsedData.ageGroup) dispatch({ type: 'SET_AGE_GROUP', payload: parsedData.ageGroup });
            if (parsedData.existingClient) dispatch({ type: 'SET_EXISTING_CLIENT', payload: parsedData.existingClient });
            if (parsedData.selectedAvailability) {
              dispatch({ type: 'SET_SELECTED_AVAILABILITY', payload: parsedData.selectedAvailability });
            }
          } catch (error) {
            // Error parsing session data
          }
        }
        
        // For refresh flow, we need to ensure the state is properly set before proceeding
        // The state updates are asynchronous, so we need to use the values we just set
        // For continue flow, use the value directly from continue response to avoid async timing issues
        let finalStateString = continueResponseStateString || state.stateString;
        let finalLocationCode = state.locationCode;
        let finalLocation = state.location;
        
        if (isPageRefresh && sessionData?.data?.data) {
          try {
            const parsedData = JSON.parse(sessionData.data.data);
            if (parsedData.stateString) finalStateString = parsedData.stateString;
            if (parsedData.locationCode) finalLocationCode = parsedData.locationCode;
            if (parsedData.location) finalLocation = parsedData.location;
          } catch (error) {
            // Error parsing session data for final state
          }
        }

        // Step 3: availabilities
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: true, loadingStep: 'availabilities', loadingProgress: 60 } });

        // Load availabilities directly without calling the full loadAvailabilities function
        // to avoid circular dependencies
        const { fetchAvailabilities } = await import('../services/availabilitiesService');
        
        if (!resolvedProviderId) {
          throw new Error('No provider ID available for fetching availabilities');
        }

        let availabilities;
        const availabilitiesPayload = { providerId: resolvedProviderId };
        try {
          availabilities = await fetchAvailabilities(config, config.remoteApiUrl, resolvedProviderId);
        } catch {
          openProviderBookedModal(availabilitiesPayload);
          dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
          dispatch({ type: 'SET_AVAILABILITIES', payload: [] });
          return;
        }

        // Dispatch the loaded data
        dispatch({ type: 'SET_AVAILABILITIES', payload: availabilities });

        // Step 4: list (locations) - Skip if no state from continue flow
        if (isContinueFlow && !hasStateFromContinue) {
          dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'availabilities', loadingProgress: 100 } });
          return; // Stop here, user needs to select state/region
        }
        
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: true, loadingStep: 'list', loadingProgress: 90 } });

        // Process locations (this is the "list" step)
        
        // Now process locations AFTER we have session and continue response data
        let availableLocations: any[] = [];
        
        // Must have availabilities to proceed (treat empty as "provider booked")
        if (!availabilities || availabilities.length === 0) {
          openProviderBookedModal(availabilitiesPayload);
          dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
          dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: [] });
          return;
        }
        
          // Get the actual regions from availability data for validation
          const regionSet = new Set(availabilities.map(a => a.region));
          const actualRegions = Array.from(regionSet);
          
          // Determine region in priority order: continue response -> session data -> first available
          let regionToUse = null;
          
          // Priority 1: Use region from continue response if it exists in availability data
          // If it doesn't exist in availability data, still use it (don't overwrite)
          if (isContinueFlow && finalStateString) {
            if (actualRegions.includes(finalStateString)) {
              regionToUse = finalStateString;
              // Don't dispatch SET_STATE_STRING here - it was already set from continue response
            } else {
              // Continue response state not in availability data, but use it anyway
              // Don't dispatch SET_STATE_STRING here - it was already set from continue response
              regionToUse = finalStateString;
            }
          }
          
          // Priority 2: Use region from session data if continue response didn't provide one
          if (!regionToUse && sessionData?.data?.data) {
            try {
              const parsedData = JSON.parse(sessionData.data.data);
              if (parsedData.stateString && actualRegions.includes(parsedData.stateString)) {
                regionToUse = parsedData.stateString;
                dispatch({ type: 'SET_STATE_STRING', payload: regionToUse });
              }
            } catch (error) {
              // Failed to parse session data for region
            }
          }
          
          // Priority 3: Fallback to first available region ONLY if we don't have a region from continue response
          if (!regionToUse && actualRegions.length > 0) {
            regionToUse = actualRegions[0];
            dispatch({ type: 'SET_STATE_STRING', payload: regionToUse });
          }
          
        // Strict validation: Must have a valid region
        if (!regionToUse) {
          throw new Error(`No valid region found. Available regions: ${actualRegions.join(', ')}. Selected region from continue response: ${state.stateString || 'none'}. Selected region from session: ${sessionData?.data?.data ? JSON.parse(sessionData.data.data).stateString || 'none' : 'none'}`);
        }
        
        // Only validate region exists in availability data if we didn't get it from continue response
        // If we got it from continue response, trust it even if not in availability data
        if (!isContinueFlow || !finalStateString) {
          if (!actualRegions.includes(regionToUse)) {
            throw new Error(`Selected region '${regionToUse}' not found in availability data. Available regions: ${actualRegions.join(', ')}`);
          }
        }
        
            // Process all available locations
            const { processAvailableLocations } = await import('../services/locationService');
            availableLocations = await processAvailableLocations({
              availabilities,
              selectedRegion: regionToUse,
              includeTelehealth: true,
              baseUrl: baseUrl
            });
        
        // Strict validation: Must have at least one location
        if (availableLocations.length === 0) {
          throw new Error(`No locations found for region '${regionToUse}'. This may indicate a data issue or API problem.`);
        }
        
        // Dispatch the processed locations
        dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: availableLocations });
        
        // Force set reason if we have specialties from continue response but reason is still not set
        if (isContinueFlow && !state.reason) {
          // This is a fallback - the continue response should have set this already
          // We need to check if the continue response data has specialties and set reason directly
        }

        // Complete
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'complete', loadingProgress: 100 } });

      } catch (error) {
        setError(error instanceof Error ? error.message : 'Unknown error occurred');
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
      }
    };

    loadDataInOrder();
  }, [dispatch]);

  // Function to resume loading when user selects state/region
  const resumeLoadingWithState = async (selectedState: string) => {
    if (isResuming) {
      return; // Prevent multiple calls
    }
    
    setIsResuming(true);
    
    try {
      setError(null);
      
      // Get mtgSession from state (saved during initial continue flow)
      const mtgSessionFromState = state.mtgSession;
      
      if (!mtgSessionFromState) {
        throw new Error('No mtgSession found in state for resume');
      }
      
      // Call continue API with locationState parameter
      const config = await initializeConfig();
      const baseUrl = config.remoteApiUrl || 'http://localhost:5293/';
      
      // Update the continue API call to include locationState
      const continueUrl = `${baseUrl}/api/Session/continue`;
      const continueResponse = await fetch(continueUrl, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          encryptedText: mtgSessionFromState,
          locationState: selectedState
        })
      });
      
      if (!continueResponse.ok) {
        const errText = await continueResponse.text();
        const dl = (window as any).dataLayer;
        if (dl) {
          dl.push({
            event: 'IOI Error',
            'IOI Error Message': `Invalid Payload: ${JSON.stringify({ encryptedText: mtgSessionFromState, locationState: selectedState })} | HTTP ${continueResponse.status}: ${errText.slice(0, 500)}`
          });
        }
        throw new Error(`Continue API failed: ${continueResponse.status}`);
      }

      const continueData = await continueResponse.json();

      const setupError = getContinueInvalidAttributeError(continueData);
      if (setupError) {
        openProviderSetupIncompleteModal(setupError);
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
        return;
      }
      
      if (continueData.success && continueData.data) {
        const data = continueData.data;
        
        // Update state with the new data including locationState
        if (data.contactId) {
          dispatch({ type: 'SET_PROVIDER_ID', payload: data.contactId.toString() });
        }
        if (data.locationState) {
          // Check if locationState contains comma (multiple states) - treat as null
          if (data.locationState.includes(',')) {
            // Don't set stateString
          } else {
            dispatch({ type: 'SET_STATE_STRING', payload: data.locationState });
          }
        }
        if (data.insurances) {
          dispatch({ type: 'SET_INSURANCE', payload: data.insurances });
        }
        if (data.specialties) {
          dispatch({ type: 'SET_REASON', payload: data.specialties });
        }
        if (data.service) {
          dispatch({ type: 'SET_SERVICE', payload: data.service });
        }
        if (data.ageRange) {
          dispatch({ type: 'SET_AGE_GROUP', payload: data.ageRange });
        }
        if (data.columnId) {
          dispatch({ type: 'SET_COLUMN_ID', payload: data.columnId });
        }
        if (data.profileId) {
          dispatch({ type: 'SET_PROFILE_ID', payload: data.profileId });
        }
        if (data.licenseKey) {
          dispatch({ type: 'SET_LICENSE_KEY', payload: data.licenseKey });
        }
        if (data.phone) {
          dispatch({ type: 'SET_PHONE', payload: data.phone });
        }
        if (data.textConsent !== undefined) {
          dispatch({ type: 'SET_TEXT_CONSENT', payload: data.textConsent });
        }
        
        // For resume, we only need to process locations since availabilities are already loaded
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: true, loadingStep: 'list', loadingProgress: 90 } });
        
        // Get existing availabilities from state
        const existingAvailabilities = state.availabilities;
        if (!existingAvailabilities || existingAvailabilities.length === 0) {
          openProviderBookedModal({ providerId: state.providerId });
          dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
          dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: [] });
          return;
        }
        
        const { processAvailableLocations } = await import('../services/locationService');
        const availableLocations = await processAvailableLocations({
          availabilities: existingAvailabilities,
          selectedRegion: selectedState,
          includeTelehealth: true,
          baseUrl: baseUrl
        });
        
        dispatch({ type: 'SET_AVAILABLE_LOCATIONS', payload: availableLocations });
        
        // Complete
        dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'complete', loadingProgress: 100 } });
      }
    } catch (error) {
      setError(error instanceof Error ? error.message : 'Unknown error occurred');
      dispatch({ type: 'SET_LOADING_STATE', payload: { isLoading: false, loadingStep: 'error', loadingProgress: 0 } });
    } finally {
      setIsResuming(false);
    }
  };

  const handleGoBack = () => {
    setShow400Error(false);
    const run = errorModalGoBack;
    setErrorModalGoBack(null);
    if (run) {
      run();
      return;
    }
    navigate(-1); // Go back to previous page
  };

  return {
    isLoading: state.isLoading,
    currentStep: state.loadingStep,
    progress: state.loadingProgress,
    error: error,
    isReady: state.loadingStep === 'complete' && !state.isLoading && !error,
    resumeLoadingWithState,
    show400Error,
    errorTitle: errorTitle || 'Technical Issues',
    errorMessage: errorMessage || 'An error has occurred, please try again.',
    handleGoBack,
    handleCloseError: () => {
      setShow400Error(false);
      setErrorModalGoBack(null);
    },
  };
}